import axios from 'axios';
import { load as Recaptcha } from 'recaptcha-v3';
import { recaptcha as reConf, api as apiConf } from '../../project.config';
import {
  CAT_BRANDS,
  CAT_COMPANIES,
  CAT_COMPONENTS,
  CAT_CONTACTS,
  CAT_PATENTS,
  CAT_PRODUCTS,
  CAT_EVENTS,
  CAT_NEWS,
  CAT_RESEARCHES,
  CAT_PATENT_RESEARCH,
  CAT_TRADEMARKS,
} from 'App/utils/Collections';
import { isAuthTokenValid } from '@/lib/Utils';
import { IDEMPOTENCY_HEADER, IdempotencyScope, isSameOrigin } from '@/app/utils/idempotency';

// Custom parameter serializer to avoid [] in arrays
function customParamsSerializer(params) {
  const urlParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (
      Array.isArray(value) &&
      (key === 'tag_id' || key === 'category_id' || key === 'domain_id' || key === 'user_identifier')
    ) {
      value.forEach((item) => urlParams.append(key, item));
    } else if (Array.isArray(value)) {
      value.forEach((item) => urlParams.append(`${key}[]`, item));
    } else if (value !== null && value !== undefined && value !== '') {
      urlParams.append(key, value);
    }
  }

  return urlParams.toString();
}

export const API_HOST_URL = apiConf.url;

const apiUrlActivitySpheres = `${API_HOST_URL}/api/activity_spheres`;
const apiUrlComponents = `${API_HOST_URL}/api/components`;
const apiUrlAdditionalFieldNames = `${API_HOST_URL}/api/additional_field_names`;
const apiUrlCategories = `${API_HOST_URL}/api/categories`;
const apiUrlCompanies = `${API_HOST_URL}/api/companies`;
const apiUrlCountries = `${API_HOST_URL}/api/countries`;
const apiUrlCustomerAddresses = `${API_HOST_URL}/api/customer_addresses`;
const apiUrlProductOrderAgentFiles = `${API_HOST_URL}/api/product_order_agent_files`;
const apiUrlProductOrderAgent = `${API_HOST_URL}/api/product_order_agent_invoices`;
const apiUrlProductMarketCountries = `${API_HOST_URL}/api/product_market_countries`;
const apiUrlCurrencies = `${API_HOST_URL}/api/currencies`;
const apiUrlCountryRegions = `${API_HOST_URL}/api/country_regions`;
const apiUrlContacts = `${API_HOST_URL}/api/contacts`;
const apiUrlProducts = `${API_HOST_URL}/api/products`;
const apiUrlProofRequests = `${API_HOST_URL}/api/proof_requests`;
const apiUrlPatents = `${API_HOST_URL}/api/patents`;
const apiUrlSearch = `${API_HOST_URL}/api/search`;
const apiUrlRecommendedProducts = `${API_HOST_URL}/api/recommended_products`;
const apiUrlNews = `${API_HOST_URL}/api/news`;
const apiUrlTrademarkHolders = `${API_HOST_URL}/api/trademark_holder_names`;
const apiUrlTrademarkRepresentatives = `${API_HOST_URL}/api/trademark_representatives`;
const apiUrlTrademarkCorrespondenceAddresses = `${API_HOST_URL}/api/trademark_correspondence_addresses`;
const apiUrlProjects = `${API_HOST_URL}/api/projects`;
const apiUrlAgents = `${API_HOST_URL}/api/agents`;
const apiUrlNewsImages = `${API_HOST_URL}/api/news_images`;
const apiUrlBrandImages = `${API_HOST_URL}/api/brand_images`;
const apiUrlUserGroups = `${API_HOST_URL}/api/usergroups`;
const apiUrlProductOrders = `${API_HOST_URL}/api/product_orders`;
const apiUrlMediaResourceTypes = `${API_HOST_URL}/api/media_resource_types`;
const apiUrlStandingReports = `${API_HOST_URL}/api/reports`;
const totalItemsPath = '/total_items';
const statistics = '/count_graph';
const apiUrlPaymentTypes = `${API_HOST_URL}/api/payment_types`;
const apiUrlModules = `${API_HOST_URL}/api/modules`;
const apiProductConsumableVariations = `${API_HOST_URL}/api/product_consumable_variations`;
const apiSampleConditions = `${API_HOST_URL}/api/sample_conditions`;
const apiSamples = `${API_HOST_URL}/api/samples`;
const apiProductOrderStatuses = `${API_HOST_URL}/api/order_statuses`;
const resetPasswordRequest = `${API_HOST_URL}/reset-password/request`;
const resetPasswordReset = `${API_HOST_URL}/reset-password/reset`;
const apiNiceClasses = `${API_HOST_URL}/api/nice_classes`;
const apiProductFiles = `${API_HOST_URL}/files/products/file`;
const apiUrlTrademarkItems = `${API_HOST_URL}/api/trademark_items`;
const apiUrlTrackingInformations = `${API_HOST_URL}/api/tracking_informations`;
const apiUrlEvents = `${API_HOST_URL}/api/events`;
const apiUrlOrderComments = `${API_HOST_URL}/api/order_comments`;
const apiUrlEconomicRegions = `${API_HOST_URL}/api/economic_regions`;
const apiUrlPriceSourceTypes = `${API_HOST_URL}/api/price_source_types`;
const apiUrlAccessLog = `${API_HOST_URL}/api/access_log`;
const apiUrlProductLines = `${API_HOST_URL}/api/product_lines`;
const apiUrlFavoriteFeatureOptions = `${API_HOST_URL}/api/favorite_user_feature_options`;
const apiUrlFavoriteUserCountries = `${API_HOST_URL}/api/favorite_user_countries`;
const apiUrlFavoriteUserTypes = `${API_HOST_URL}/api/favorite_user_types`;
const apiUrlFavoriteUserNiceClasses = `${API_HOST_URL}/api/favorite_user_nice_classes`;

class BackendModel {
  config = {};
  constructor(config) {
    this._source = axios.CancelToken.source();
    this.config = {
      apiHost: API_HOST_URL,
      resetPasswordRequest,
      resetPasswordReset,
      apiUrlActivitySpheres,
      apiUrlProducts,
      apiUrlComponents,
      apiUrlCompanies,
      apiUrlCountries,
      apiUrlProductMarketCountries,
      apiUrlCountryRegions,
      apiUrlEconomicRegions,
      apiUrlContacts,
      apiUrlProofRequests,
      apiUrlPatents,
      apiUrlSearch,
      apiUrlNews,
      apiUrlProjects,
      apiUrlTrademarkHolders,
      apiUrlTrademarkRepresentatives,
      apiUrlTrademarkCorrespondenceAddresses,
      apiUrlNewsImages,
      apiUrlBrandImages,
      apiUrlMediaResourceTypes,
      apiUrlStandingReports,
      apiProductConsumableVariations,
      apiUrlModules,
      apiUrlUserGroups,
      apiUrlProductOrderAgentFiles,
      apiUrlProductOrderAgent,
      apiSamples,
      apiUrlCustomerAddresses,
      apiUrlAgents,
      apiSampleConditions,
      apiUrlCategories,
      apiUrlAdditionalFieldNames,
      apiNiceClasses,
      apiUrlRecommendedProducts,
      apiProductFiles,
      apiProductOrderStatuses,
      apiUrlEvents,
      apiUrlCurrencies,
      apiUrlPaymentTypes,
      apiUrlOrderComments,
      apiUrlTrackingInformations,
      apiUrlPriceSourceTypes,
      apiUrlAccessLog,
      apiUrlUsers: `${API_HOST_URL}/api/users`,
      apiUrlResearchesAuthors: `${API_HOST_URL}/api/research_authors`,
      apiUrlResearchesDomains: `${API_HOST_URL}/api/research_domains`,
      apiUrlResearchesCategories: `${API_HOST_URL}/api/research_categories`,
      apiUrlResearches: `${API_HOST_URL}/api/researches`,
      apiUrlRoles: `${API_HOST_URL}/api/roles`,
      apiCourierDeliveryServises: `${API_HOST_URL}/api/courier_delivery_services`,
      apiUrlCustomers: `${API_HOST_URL}/api/customers`,
      apiUrlUsersStaffStatistics: `${API_HOST_URL}/api/users/staff_statistics`,
      apiComponentPackages: `${API_HOST_URL}/api/component_packages`,
      apiComponentCategories: `${API_HOST_URL}/api/component_categories`,
      apiUrlPatentsExplore: `${apiUrlPatents}/explore`,
      apiUrlLogin: `${API_HOST_URL}/login_check`,
      apiUrlRefresh: `${API_HOST_URL}/api/token/refresh`,
      apiUrlLeads: `${API_HOST_URL}/api/leads`,
      apiUrlUser: `${API_HOST_URL}/api/users/profile`,
      apiUrlProductsTotal: `${apiUrlProducts}${totalItemsPath}`,
      apiUrlProductsStats: `${apiUrlProducts}/chart`,
      apiUrlComponentsTotal: `${apiUrlComponents}${totalItemsPath}`,
      apiUrlComponentsStats: `${apiUrlComponents}${statistics}`,
      apiUrlCompaniesTotal: `${apiUrlCompanies}${totalItemsPath}`,
      apiUrlCompaniesStats: `${apiUrlCompanies}${statistics}`,
      apiUrlCountriesTotal: `${apiUrlCountries}${totalItemsPath}`,
      apiUrlContactsTotal: `${apiUrlContacts}${totalItemsPath}`,
      apiUrlTrademarksTotal: `${apiUrlTrademarkItems}${totalItemsPath}`,
      apiUrlPatentsTotal: `${apiUrlPatents}${totalItemsPath}`,
      apiUrlContactsStats: `${apiUrlContacts}${statistics}`,
      apiUrlTrademarks: `${API_HOST_URL}/api/trademark_items`,
      apiUrlReports: `${API_HOST_URL}/api/products/report`, // deprecated
      apiUrlReportGenerator: `${API_HOST_URL}/api/report_generator`,
      apiUrlProductStatuses: `${API_HOST_URL}/api/production_statuses`,
      apiUrlProductBrands: `${API_HOST_URL}/api/brands`,
      apiUrlProductFeatures: `${API_HOST_URL}/api/features`,
      apiUrlProductFeatureGroups: `${API_HOST_URL}/api/feature_groups`,
      apiUrlProductFeatureOptions: `${API_HOST_URL}/api/feature_options`,
      apiUrlCompanyCategories: `${API_HOST_URL}/api/company_categories`,
      apiUrlComments: `${API_HOST_URL}/api/comments`,
      apiUrlTypes: `${API_HOST_URL}/api/types`,
      apiAnalysisTypes: `${API_HOST_URL}/api/analysis_types`,
      apiProductAnalysisOrders: `${API_HOST_URL}/api/product_analysis_orders`,
      apiUrlSuffix: '.jsonld',
      apiUrlProductOrders,
      apiUrlProductImages: `${API_HOST_URL}/api/product_images`,
      apiUrlCompanyImages: `${API_HOST_URL}/api/company_images`,
      apiUrlPatentContents: `${API_HOST_URL}/api/patent_contents`,
      apiUrlPatentDescriptions: `${API_HOST_URL}/api/patent_descriptions`,
      apiUrlPatentClaims: `${API_HOST_URL}/api/patent_claims`,
      apiUrlPatentCpcClassification: `${API_HOST_URL}/api/patents/classification/cpc`,
      apiUrlPatentIpcClassification: `${API_HOST_URL}/api/patents/classification/ipc`,
      apiUrlUserBookmarks: `${API_HOST_URL}/api/user_bookmarks`,
      apiUrlUserFilters: `${API_HOST_URL}/api/user_filters`,
      apiUrlFilesGroups: `${API_HOST_URL}/api/file_groups`,
      apiFiles: `${API_HOST_URL}/api/files`,
      apiOrderStatuses: `${API_HOST_URL}/api/order_statuses`,
      apiCpcClassifications: `${API_HOST_URL}/api/patents/classification/cpc`,
      apiIpcClassifications: `${API_HOST_URL}/api/patents/classification/ipc`,
      apiPatents: `${API_HOST_URL}/api/patents`,
      apiPatentResearch: `${API_HOST_URL}/api/patent_researches`,
      apiPatentResearchImages: `${API_HOST_URL}/api/patent_research_images`,
      apiPatentResearchDocument: `${API_HOST_URL}/api/patent_research_documents`,
      apiPatentResearchDocumentComment: `${API_HOST_URL}/api/patent_research_document_comments`,
      apiPatentCitations: `${API_HOST_URL}/api/patent_citations`,
      apiUrlTrademarkStatuses: `${API_HOST_URL}/api/trademark_statuses`,
      apiUrlsTrademarkOffices: `${API_HOST_URL}/api/trademark_offices`,
      apiPatentInternalNotes: `${API_HOST_URL}/api/patent_internal_notes`,
      apiUrlTrackingInformationContents: `${API_HOST_URL}/api/tracking_information_contents`,
      apiUrlTrackingInformation: `${API_HOST_URL}/api/tracking_informations`,
      apiUrlTrademarksSuggestions: `${API_HOST_URL}/api/trademark_items/suggestion`,
      apiUrlCountriesProductMarkets: `${API_HOST_URL}/api/countries/product_markets`,
      apiUrlProductVariations: `${API_HOST_URL}/api/product_variations`,
      apiUrlProductVariationImages: `${API_HOST_URL}/api/product_variation_images`,
      apiUrlInformationSources: `${API_HOST_URL}/api/information_sources`,
      apiUrlInformationSourceSnapshots: `${API_HOST_URL}/api/information_source_snapshots`,
      apiUrlEventTypes: `${API_HOST_URL}/api/event_types`,
      apiUrlEventThemes: `${API_HOST_URL}/api/event_themes`,
      apiUrlEventAccesses: `${API_HOST_URL}/api/event_accesses`,
      apiUrlEventStatus: `${API_HOST_URL}/api/event_statuses`,
      apiUrlEventAtendingRequest: `${API_HOST_URL}/api/event_attending_requests`,
      apiUrlEventImages: `${API_HOST_URL}/api/event_images`,
      apiUrlAttendingRequests: `${API_HOST_URL}/api/event_attending_requests`,
      apiUrlMarketCountriesFiles: `${API_HOST_URL}/files/product_market_countries`,
      apiUrlChangePassword: `${API_HOST_URL}/change-password`,
      apiUrlOrderCommentsFiles: `${API_HOST_URL}/api/order_comment_files`,
      staffStats: {
        [CAT_BRANDS]: `${API_HOST_URL}/api/brand_staff_statistics`,
        [CAT_COMPANIES]: `${API_HOST_URL}/api/company_staff_statistics`,
        [CAT_COMPONENTS]: `${API_HOST_URL}/api/component_staff_statistics`,
        [CAT_CONTACTS]: `${API_HOST_URL}/api/contact_staff_statistics`,
        [CAT_PATENTS]: `${API_HOST_URL}/api/patent_staff_statistics`,
        [CAT_PRODUCTS]: `${API_HOST_URL}/api/product_staff_statistics`,
        [CAT_EVENTS]: `${API_HOST_URL}/api/event_staff_statistics`,
        [CAT_NEWS]: `${API_HOST_URL}/api/news_staff_statistics`,
        [CAT_RESEARCHES]: `${API_HOST_URL}/api/research_staff_statistics`,
        [CAT_PATENT_RESEARCH]: `${API_HOST_URL}/api/patent_research_staff_statistics`,
        [CAT_TRADEMARKS]: `${API_HOST_URL}/api/trademark_staff_statistics`,
      },
      staffStatsItem: {
        [CAT_BRANDS]: `${API_HOST_URL}/api/brand_staff_item_statistics`,
        [CAT_COMPANIES]: `${API_HOST_URL}/api/company_staff_item_statistics`,
        [CAT_COMPONENTS]: `${API_HOST_URL}/api/component_staff_item_statistics`,
        [CAT_CONTACTS]: `${API_HOST_URL}/api/contact_staff_item_statistics`,
        [CAT_PATENTS]: `${API_HOST_URL}/api/patent_staff_item_statistics`,
        [CAT_PRODUCTS]: `${API_HOST_URL}/api/product_staff_item_statistics`,
        [CAT_EVENTS]: `${API_HOST_URL}/api/event_staff_statistics`,
        [CAT_NEWS]: `${API_HOST_URL}/api/news_staff_item_statistics`,
        [CAT_RESEARCHES]: `${API_HOST_URL}/api/research_staff_item_statistics`,
      },
      apiUrlProductLines,
      apiUrlFavoriteFeatureOptions,
      apiUrlFavoriteUserCountries,
      apiUrlFavoriteUserTypes,
      apiUrlFavoriteUserNiceClasses,
      ...(config || {}),
    };
    this.skipErrorChecking = [this.config.apiUrlChangePassword];
    // this._loadRecaptcha(reConf.siteKey).then();
  }
  async _loadRecaptcha(key) {
    return Recaptcha(key, { autoHideBadge: true }).then((recaptcha) => {
      this._recaptcha = recaptcha;
      return recaptcha;
    });
  }
  _token = '';
  _refresh_token = '';
  _onError;
  _onRefreshToken;
  _showDrafts = false;
  _idempotencyScope = null;
  _idempotencyQueue = Promise.resolve();
  refreshToken = async (refresh_token) => {
    return this.post({
      url: this.config.apiUrlRefresh,
      data: { refresh_token },
      is_protected: true,
      auth: false,
    })
      .then((res) => res.data)
      .catch();
  };
  /**
   * @param {number} [minTtl] renew the token when it expires within `minTtl` seconds — for the
   * calls that hand the token over to a service which keeps working with it after the response
   * (see ASSISTANT_MIN_TOKEN_TTL). Zero keeps the default behaviour: renew only once expired.
   */
  async validateToken(minTtl = 0) {
    if (!isAuthTokenValid(this._token, minTtl)) {
      if (this._refresh_token) {
        const tokens = await this.refreshToken(this._refresh_token).catch(() => {
          return false;
        });
        if (typeof this._onRefreshToken === 'function') {
          this._onRefreshToken(tokens);
        }
        this.setToken(tokens);
      } else {
        return false;
      }
    }
    return this._token;
  }
  async getAuthHeaders(restHeaders = {}, minTtl = 0) {
    const headers = { ...(restHeaders || {}) };
    if (this._token) {
      const token = await this.validateToken(minTtl);
      if (token === false) {
        return false;
      }
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }
  getRecaptchaToken = async (action) => {
    try {
      if (!this._recaptcha) {
        await this._loadRecaptcha(reConf.siteKey);
      }
      return await this._recaptcha.execute(action);
    } catch (e) {
      throw new Error('Can not initialize ReCaptcha');
    }
  };
  CatchError = (err) => {
    if (typeof this._onError === 'function') {
      this._onError(err);
    }
  };
  setToken = ({ token, refresh_token }) => {
    this._token = token;
    this._refresh_token = refresh_token;
  };
  get = async (options, clearParams = false) => {
    // eslint-disable-next-line no-unused-vars
    const { url, method, params, ...rest } = options;
    const _params = params || {};
    if (!clearParams) {
      if (!('draft' in _params)) {
        if (!this._showDrafts) {
          _params.draft = false;
        }
      }
    }
    return this.request(url, 'get', {
      ...rest,
      contentType: 'application/json; charset=utf-8',
      params: _params,
    });
  };
  post = async (options = {}) => {
    // eslint-disable-next-line no-unused-vars
    const { url, method, ...rest } = options;
    return this.request(url, 'post', rest);
  };
  patch = (options = {}) => {
    // eslint-disable-next-line no-unused-vars
    const { url, method, ...rest } = options;
    return this.request(url, 'patch', rest);
  };
  put = (options) => {
    // eslint-disable-next-line no-unused-vars
    const { url, method, ...rest } = options || {};
    return this.request(url, 'put', rest);
  };
  delete = (options) => {
    // eslint-disable-next-line no-unused-vars
    const { url, method, params, ...rest } = options || {};
    return this.request(url, 'delete', { ...rest, data: params });
  };
  download = async (options) => {
    const { url, ...rest } = options;
    const config = {
      ...rest,
      contentType: 'application/json; charset=utf-8',
      responseType: 'arraybuffer',
      headers: await this.getAuthHeaders(),
    };

    return this.request(url, 'post', config);
  };
  loadData = (options) => {
    return this.get({ ...options, responseType: 'arraybuffer' });
  };
  loadBlobData = (options) => {
    return this.get({ ...options, responseType: 'blob' });
  };
  postJSON = (data, options = {}) => {
    const { params, ...rest } = options;
    return this.post({
      ...rest,
      headers: {
        ...(options?.headers || {}),
        'Content-Type': 'application/json',
      },
      params,
      data: JSON.stringify(data),
    });
  };
  /**
   * Runs `fn` with an operation-level idempotency key: every POST to the main backend issued
   * while `fn` is pending gets a key derived from `key`. Pass the same key when the user retries
   * the operation, so the backend replays the original response instead of creating a duplicate.
   *
   * The scope is a single shared slot, so keyed operations run one at a time: overlapping ones would
   * hand their keys to each other's requests. For the same reason `fn` must not call this method
   * again — the nested call would wait for its own parent and never start.
   */
  withIdempotencyKey = (key, fn) => {
    if (!key) {
      return fn();
    }
    const run = async () => {
      this._idempotencyScope = new IdempotencyScope(key);
      try {
        return await fn();
      } finally {
        this._idempotencyScope = null;
      }
    };
    const result = this._idempotencyQueue.then(run);
    this._idempotencyQueue = result.catch(() => {});
    return result;
  };
  /**
   * Only the main backend supports the header; external services may reject it in CORS preflight.
   * Unauthenticated calls (token refresh, login) are skipped: a replayed refresh response would
   * hand back stale tokens. Outside of a scope a key is sent only when passed in `idempotencyKey`:
   * a fresh key per call would not survive a user retry, so it would not prevent duplicates.
   */
  resolveIdempotencyKey(url, method, idempotencyKey, auth) {
    if (method !== 'post' || auth === false || !isSameOrigin(url, API_HOST_URL)) {
      return null;
    }
    if (idempotencyKey) {
      return idempotencyKey;
    }
    return this._idempotencyScope ? this._idempotencyScope.keyFor(url) : null;
  }
  async request(url, method = 'get', options = {}) {
    let { is_protected, auth, headers, ld, minTokenTtl, idempotencyKey, ...rest } = options || {};
    if (auth === void 0) {
      auth = true;
    }
    // Resolved before any await: scope ordinals must follow the order requests are issued in
    const requestIdempotencyKey = this.resolveIdempotencyKey(url, method, idempotencyKey, auth);
    rest.headers = auth === false ? headers || {} : await this.getAuthHeaders(headers, minTokenTtl);
    if (rest.headers === false) {
      return;
    }
    if (requestIdempotencyKey) {
      rest.headers[IDEMPOTENCY_HEADER] = requestIdempotencyKey;
    }
    if (is_protected) {
      const [path] = new URL(url).pathname
        .replace('/api', '')
        .replace(/[^A-Za-z/_]/g, '_')
        .substr(1)
        .split('.');
      rest.headers['recaptcha'] = await this.getRecaptchaToken(`${method}_${path}`);
    }
    if (ld) {
      rest.headers['Accept'] = 'application/ld+json';
    }
    const needsCustomSerialization =
      rest.params &&
      Object.keys(rest.params).some((key) => {
        const value = rest.params[key];
        return (
          Array.isArray(value) &&
          (key === 'tag_id' || key === 'category_id' || key === 'domain_id' || key === 'user_identifier')
        );
      });

    const axiosConfig = {
      url,
      method,
      ...rest,
      cancelToken: this._source.token,
    };

    if (needsCustomSerialization) {
      axiosConfig.paramsSerializer = customParamsSerializer;
    }

    return axios(axiosConfig).catch((err) => {
      if (!this.skipErrorChecking.includes(url)) {
        this.CatchError(err);
      }
      throw err;
    });
  }
  get token() {
    return this._token;
  }
  set onError(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }
    this._onError = callback;
  }
  set onRefreshToken(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }
    this._onRefreshToken = callback;
  }
  set showDrafts(state) {
    this._showDrafts = state || false;
  }

  cancel = () => {
    if (this._source) {
      this._source.cancel();
      this._source = axios.CancelToken.source();
    }
  };
  testFail = () => {
    const err = { code: 401, message: 'Expired JWT Token' };
    this.CatchError(err);
  };
}

const Backend = new BackendModel();

export default Backend;
